import { type FC } from "react";

import { Card, CardRowSimple, type GridCard, Tag, TagIntent } from "widgets";
import { formatNumberByThousands } from "widgets/utils";

import style from "../../core/cards/styles/Card.module.scss";

import { getGeneTypeColor } from "../../../app-utils/color.js";
import type { Gene } from "../../../graphql.js";
import { ContigTile, GeneTile, GenomeViewTile } from "../../core/tiles/index.js";

export interface IGeneCardProps extends GridCard {
  gene: Gene;
  isEntitySummary?: boolean;
}

const GeneInfo: FC<{ gene: Gene }> = ({ gene }) => {
  if (gene == undefined) return null;

  const region = gene.region;
  const startFormatted = formatNumberByThousands(region.range.start);
  const stopFormatted = formatNumberByThousands(region.range.stop);

  return (
    <>
      <CardRowSimple>
        <GeneTile gene={gene} />
        <GenomeViewTile
          title={`${startFormatted} – ${stopFormatted} (${region.strand})`}
          footNote={`${formatNumberByThousands(region.range.stop - region.range.start + 1)} bp`}
          to={`/gene/${gene.id}/genome`} // Range is not mandatory and the GenomeView of an entity is automatically set.
        />
      </CardRowSimple>
      {gene.synonyms && gene.synonyms.length > 0 && (
        <CardRowSimple label={"Synonyms"}>
          <div className={style.tags_list}>
            {gene.synonyms.map((syn) =>
              syn != undefined ? <Tag label={syn.trim()} intentColor={TagIntent.Secondary} /> : null,
            )}
          </div>
        </CardRowSimple>
      )}
      {region?.contig && (
        <CardRowSimple label={"Contig"}>
          <ContigTile contig={region.contig} type={null} />
        </CardRowSimple>
      )}
    </>
  );
};

const DetailedGeneInfo: FC<{ gene: Gene }> = ({ gene }) => {
  if (gene == undefined) return null;

  const region = gene.region;
  const startFormatted = formatNumberByThousands(region.range.start);
  const stopFormatted = formatNumberByThousands(region.range.stop);

  return (
    <>
      <CardRowSimple label={"Type"}>
        <div className={style.tags_list}>
          <Tag label={gene.geneType} intentColor={getGeneTypeColor(gene.geneType)} />
        </div>
      </CardRowSimple>
      <CardRowSimple label={"Name"} value={gene.name} />
      {gene.synonyms && gene.synonyms.length > 0 && (
        <CardRowSimple label={"Synonyms"}>
          <div className={style.tags_list}>
            {gene.synonyms.map((syn) =>
              syn != undefined ? <Tag label={syn.trim()} intentColor={TagIntent.Secondary} /> : null,
            )}
          </div>
        </CardRowSimple>
      )}
      {region?.contig && (
        <CardRowSimple label={"Contig"}>
          <ContigTile contig={region.contig} type={null} />
        </CardRowSimple>
      )}
      <CardRowSimple label={"Range"}>
        <GenomeViewTile
          title={`${startFormatted} – ${stopFormatted} (${region.strand})`}
          to={`/gene/${gene.id}/genome`} // Range is not mandatory and the GenomeView of an entity is automatically set.
          footNote={`${formatNumberByThousands(region.range.stop - region.range.start + 1)} bp`}
        />
      </CardRowSimple>
      <CardRowSimple key={"description"} label={"Description"} value={gene.description} />
    </>
  );
};

const GeneCard: FC<IGeneCardProps> = (props) => {
  const gene: Gene = props.gene;
  if (!gene) {
    return null;
  }

  return (
    <Card key="gene-info-card" title={props.title ?? "Gene Info"}>
      {props.isEntitySummary ? <DetailedGeneInfo gene={gene} /> : <GeneInfo gene={gene} />}
    </Card>
  );
};

export { GeneCard, GeneInfo };
